import { useCallback, useState } from 'react';
import { DatePickerStateProvider } from '@rehookify/datepicker';
import CalendarComponent from '~/containers/Calendar/Calendar';
import Alert from '~/components/General/Alert';
import CalendarPax from '~/components/Calendar/Pax';
import useTranslation from '~/hooks/useTranslation';
import styles from '~/styles/calendar/calendar.module.css';
import buttonStyles from '~/styles/button.module.css';
import utilStyles from '~/styles/utils.module.css';
import formStyles from '~/styles/form.module.css';
import BookingButton from '~/components/Calendar/BookingButton';
import { DayState } from '~/hooks/calendar/useDayStateForDate';
import ContactButton from '~/containers/Calendar/ContactButton';
import { InferredCalendarOptions } from '~/types/options';
import usePackagePeriod from '~/hooks/calendar/usePackagePeriod';
import { useOptions } from '~/hooks/useOptions';
import parsePackageOptions from '~/helpers/parsePackageOptions';
import PlainSelectField from '~/components/Form/PlainSelectField';
import cnh from '~/helpers/cnh';
import formatDate from '~/helpers/formatDate';

function Calendar() {
  const options = useOptions() as InferredCalendarOptions;
  const translate = useTranslation();

  // Dates that are select by the user, this is an array because
  // it also supports ranges.
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  // Offset date is the current visible date, most likely the start
  // of a month.
  const [offsetDate, setOffsetDate] = useState<Date>(new Date());

  const packageOptions = parsePackageOptions(options.packageId);

  const [pax, setPax] = useState(options.initialPax);
  const [activePackageId, setActivePackageId] = useState<number>(
    packageOptions[0].packageId,
  );

  // Retrieve package data
  const {
    data: packagePeriodData,
    error: packagePeriodError,
    isPending: packagePeriodPending,
  } = usePackagePeriod({
    package_id: activePackageId,
    locale: options.locale,
    office_id: options.officeId,
    department_id: options.departmentId,
  });

  const onDatesChange = useCallback((dates: Date[]) => {
    setSelectedDates(dates);
  }, []);

  const onOffsetChange = useCallback((date: Date) => {
    setOffsetDate(date);
  }, []);

  const onChangePackage = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    setActivePackageId(Number(event.target.value));
  }, []);

  const onConfirmButtonRender = useCallback((selectedDayState: DayState | null) => {
    // If no date is selected, don't show a button
    if (!selectedDayState) {
      return (
        <div
          className={cnh(
            buttonStyles.btn,
            buttonStyles.btnMute,
            buttonStyles.btnFull,
            utilStyles.mbSmall,
          )}
        >
          {translate('calendar.select_date')}
        </div>
      );
    }

    // If the day has a price, show a booking button
    if (selectedDayState.price) {
      return (
        <BookingButton
          selectedDayState={selectedDayState}
          pax={pax}
          packageId={activePackageId}
        />
      );
    }

    return (
      <ContactButton
        selectedDayState={selectedDayState}
        pax={pax}
        packageId={activePackageId}
      />
    );
  }, [pax, activePackageId, translate]);

  const onContactButtonRender = useCallback(() => {
    return (
      <ContactButton
        pax={pax}
        packageId={activePackageId}
      />
    );
  }, [pax, activePackageId]);

  // If the package is loading, dont' render the component but show a loading message
  if (packagePeriodPending || packagePeriodData === undefined) {
    return (
      <div className={styles.loading}>
        {translate('calendar.loading')}
      </div>
    );
  }

  // If the package load failed, show an error message and don't render the component
  if (packagePeriodError) {
    return (
      <Alert
        message={packagePeriodError.message}
      />
    );
  }

  // The package should return a range of availabilities, with
  // this range, we can define what months to show in the calendar.
  const minDate = new Date(packagePeriodData.price_period?.start_date ?? new Date());
  const maxDate = new Date(packagePeriodData.price_period?.end_date ?? new Date());

  // If the current date in the calendar, is before the available
  // dates from the package, move to the first available month.
  if (offsetDate.getTime() < minDate.getTime()) {
    setOffsetDate(minDate);
  }

  const maxPaxError = pax > options.maxPax;

  return (
    <div className={styles.calenderLayoutWrapper}>
      {import.meta.env.VITE_ENABLE_DATA_AVAILABILITY_WARNING === 'true' && (
        <div className={utilStyles.warningNote}>
          <div>
            The data on this environment is not kept up to date. We can only connect one
            environment to TravelSpirit. And that environment is the production environment.
          </div>
          <div>
            This environment requires manual data updates.
          </div>
          <div>
            Last synced:
            {' '}
            {packagePeriodData.synced_at
              ? formatDate(packagePeriodData.synced_at, 'd-m-Y H:i')
              : 'never'}
          </div>
        </div>
      )}

      {packageOptions.length > 1 && (
        <div className={formStyles.formGroup}>
          <PlainSelectField
            label={translate('calendar.package')}
            value={activePackageId}
            options={packageOptions.map((packageOption) => ({
              label: packageOption.packageName || '',
              value: packageOption.packageId,
            }))}
            onChange={onChangePackage}
          />
        </div>
      )}

      <div className={formStyles.formGroup}>
        <CalendarPax
          pax={pax}
          setPax={setPax}
          minPax={options.minPax}
        />
      </div>

      {maxPaxError && (
        <div>
          <Alert
            message={`${translate('calendar.max_pax_error', { max: options.maxPax })}`}
          />

          <ContactButton
            pax={pax}
            packageId={activePackageId}
          />
        </div>
      )}

      {!maxPaxError && (
        <DatePickerStateProvider
          config={{
            selectedDates,
            offsetDate,
            onDatesChange,
            onOffsetChange,
            locale: {
              locale: options.locale,
            },
            calendar: {
              startDay: 0,
            },
            dates: {
              minDate,
              maxDate,
            },
          }}
        >
          <CalendarComponent
            pax={pax}
            packageId={activePackageId}
            offsetDate={offsetDate}
            setOffsetDate={setOffsetDate}
            minDate={minDate}
            maxDate={maxDate}
            confirmButton={onConfirmButtonRender}
            contactButton={onContactButtonRender}
          />
        </DatePickerStateProvider>
      )}

    </div>
  );
}

export default Calendar;
